/**
 * @feature quote-requests
 * @layer presentation/components
 *
 * AttachmentsSection - 첨부파일
 * Displays file attachments with preview capability
 */

import { type FC } from "react";
import { Paperclip, FileText, Eye } from "@semo/icons";
import { cn } from "../../../utils/cn";
import type { QuoteReviewSectionProps, AttachmentMetadataDto } from "../types";

interface AttachmentsSectionProps extends QuoteReviewSectionProps {
  /** Optional callback to handle file preview */
  onPreviewFile?: (attachment: AttachmentMetadataDto) => void;
}

/**
 * Attachments Section Component
 * Shows file attachments with preview button
 */
export const AttachmentsSection: FC<AttachmentsSectionProps> = ({
  data,
  onEdit,
  onPreviewFile,
}) => {
  // Extract attachments
  const isAttachment = (item: unknown): item is AttachmentMetadataDto =>
    typeof item === "object" && item !== null;

  const attachments: AttachmentMetadataDto[] = Array.isArray(data.attachments)
    ? data.attachments.filter(isAttachment)
    : [];
  const isEmpty = attachments.length === 0;

  return (
    <div className="bg-white rounded-[20px] p-6 sm:p-8 relative overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 relative z-10">
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className="w-10 h-10 rounded-[12px] bg-primary/5 flex items-center justify-center text-primary">
            <Paperclip className="w-5 h-5" />
          </div>
          {/* Title & Count */}
          <div className="flex items-center gap-2.5">
            <h3 className="text-lg font-semibold text-gray-900">첨부파일</h3>
            {!isEmpty ? (
              <span className="text-gray-400 font-medium text-sm">
                {attachments.length}
              </span>
            ) : (
              <span className="text-sm text-gray-400 font-medium">
                없음
              </span>
            )}
          </div>
        </div>

        {/* Edit button - optional */}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={cn(
              "px-4 py-1.5",
              "text-gray-400",
              "rounded-[10px]",
              "hover:bg-gray-50 hover:text-primary",
              "transition-all",
              "text-sm font-semibold",
            )}
          >
            수정
          </button>
        )}
      </div>

      {/* Content - File List (Show only if not empty) */}
      {!isEmpty && (
        <div className="relative z-10">
          <div className="space-y-2">
            {attachments.map(
              (attachment: AttachmentMetadataDto, index: number) => (
                <div
                  key={`${attachment.url}-${index}`}
                  className="flex items-center gap-3 px-3 py-2.5 bg-gray-50/50 rounded-[12px] hover:bg-gray-100/50 transition-all group"
                >
                  {/* File Icon */}
                  <FileText className="w-4 h-4 text-primary flex-shrink-0" />

                  {/* File Info */}
                  <p className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate">
                    {attachment.fileName}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary font-semibold hover:underline"
                    >
                      다운로드
                    </a>
                    {onPreviewFile && (
                      <button
                        type="button"
                        onClick={() => onPreviewFile(attachment)}
                        className="text-gray-400 hover:text-primary transition-all"
                        title="파일 미리보기"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
};
